/**
 * Native, view-only PDF embed for article pages.
 *
 * Fetches the PDF as raw bytes and renders each page to a <canvas> with
 * PDF.js, instead of pointing an <iframe>/<a> at the file directly. This
 * keeps the page free of a clickable/downloadable link to the source file
 * and avoids a selectable text layer.
 *
 * Not a guarantee against copying — the file is still fetched over the
 * network (visible in browser dev tools) and pages can be screenshotted.
 */
import * as pdfjsLib from 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.9.155/pdf.min.mjs';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.9.155/pdf.worker.min.mjs';

const PAGE_BATCH = 3;

function initPdfViewer(root) {
  const url = root.getAttribute('data-pdf-url');
  if (!url) return;

  root.addEventListener('contextmenu', (event) => event.preventDefault());

  const pagesContainer = root.querySelector('[data-pdf-pages]');
  const loadingEl = root.querySelector('[data-pdf-loading]');
  const moreBtn = root.querySelector('[data-pdf-load-more]');

  let pdfDoc = null;
  let nextPage = 1;

  function renderPage(pageNumber) {
    return pdfDoc.getPage(pageNumber).then((page) => {
      const targetWidth = root.clientWidth || 800;
      const baseViewport = page.getViewport({ scale: 1 });
      const cssScale = Math.min(2, targetWidth / baseViewport.width);
      const viewport = page.getViewport({ scale: cssScale });

      // Render at the display's actual pixel density (Retina/HiDPI),
      // not just the CSS size, or pages come out soft/pixelated.
      const outputScale = window.devicePixelRatio || 1;

      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      canvas.setAttribute('role', 'img');
      canvas.setAttribute('aria-label', `Page ${pageNumber}`);
      pagesContainer.appendChild(canvas);

      const ctx = canvas.getContext('2d');
      const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;
      return page.render({ canvasContext: ctx, viewport, transform }).promise;
    });
  }

  function renderNextBatch() {
    const batchEnd = Math.min(nextPage + PAGE_BATCH - 1, pdfDoc.numPages);
    let chain = Promise.resolve();

    for (let pageNumber = nextPage; pageNumber <= batchEnd; pageNumber += 1) {
      chain = chain.then(() => renderPage(pageNumber));
    }

    return chain.then(() => {
      nextPage = batchEnd + 1;
      moreBtn.hidden = nextPage > pdfDoc.numPages;
    });
  }

  fetch(url)
    .then((res) => res.arrayBuffer())
    .then((buffer) => pdfjsLib.getDocument({ data: buffer }).promise)
    .then((doc) => {
      pdfDoc = doc;
      loadingEl.hidden = true;
      return renderNextBatch();
    })
    .catch((err) => {
      loadingEl.textContent = "This document couldn't be loaded.";
      // eslint-disable-next-line no-console
      console.error('PDF viewer error:', err);
    });

  moreBtn.addEventListener('click', renderNextBatch);
}

document.querySelectorAll('[data-pdf-viewer]').forEach(initPdfViewer);
